import { billingAccessCopy } from "@/lib/billingAccessCopy";
import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  BriefcaseBusiness,
  CheckCircle2,
  Mail,
  MapPin,
  MoreHorizontal,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  UserRound,
  UserRoundPlus,
  UsersRound,
} from "lucide-react";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAuth, useWorkspace } from "@/lib/store";
import { useLocalization } from "@/lib/localization";
import { hasPermission, STAFF_PERMISSIONS } from "@/lib/permissions";
import { deleteStaff, inviteStaff, updateStaff } from "@/lib/shopApi";
import { toast } from "sonner";
import { z } from "zod";

const permissionIds = [...STAFF_PERMISSIONS];
const roleSchema = z.enum(["Staff", "Manager"]);
const enumSchema = z.enum(permissionIds);
const roleOptions = ["Staff", "Manager"];

function getPermissionOptions(copy, settingsCopy, billingCopy) {
  return [
    { id: "sell", label: copy.permissionSell },
    { id: "history", label: copy.permissionHistory },
    { id: "inventory", label: copy.permissionInventory },
    { id: "debts", label: copy.permissionDebts },
    { id: "expenses", label: copy.permissionExpenses },
    { id: "analytics", label: copy.permissionAnalytics },
    { id: "assistant", label: settingsCopy.permissionAssistant },
    { id: "notifications", label: copy.permissionNotifications },
    { id: "staff_access", label: copy.permissionStaffAccess },
    { id: "branch_management", label: copy.permissionBranchManagement },
    { id: "settings", label: settingsCopy.permissionSettings },
    { id: "billing", label: billingCopy.label },
  ];
}

function getPresets(copy) {
  return [
    {
      id: "basic_seller",
      label: copy.presetBasicSeller,
      description: copy.presetBasicSellerDesc,
      permissions: ["sell", "history"],
    },
    {
      id: "operations",
      label: copy.presetOperations,
      description: copy.presetOperationsDesc,
      permissions: ["sell", "history", "inventory", "debts", "expenses", "notifications"],
    },
    {
      id: "full_access",
      label: copy.presetFullAccess,
      description: copy.presetFullAccessDesc,
      permissions: [...permissionIds],
    },
  ];
}

function createPermissionsSchema(copy) {
  return z.array(enumSchema).min(1, copy.chooseAccessLevel);
}

function createInviteStaffSchema(copy) {
  return z.object({
    branchId: z.string().trim().min(1, copy.chooseBranch),
    name: z.string().trim().min(2, copy.enterFullName),
    permissions: createPermissionsSchema(copy),
    email: z.string().trim().email("Enter a valid email address."),
    presetId: z.string().min(1),
    role: roleSchema,
  });
}

function createEditStaffSchema(copy) {
  return z.object({
    branchId: z.string().trim().min(1, copy.chooseBranch),
    name: z.string().trim().min(2, copy.enterFullName),
    permissions: createPermissionsSchema(copy),
    presetId: z.string().min(1),
    role: roleSchema,
  });
}

function createInviteForm(presets, branchId = "") {
  return {
    branchId,
    name: "",
    permissions: presets[0].permissions,
    email: "",
    presetId: presets[0].id,
    role: roleOptions[0],
  };
}

function getInitials(name, fallback = "") {
  return String(name || fallback)
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function findPresetId(presets, permissions = []) {
  const preset = presets.find((item) => (
    item.permissions.length === permissions.length &&
    item.permissions.every((permission) => permissions.includes(permission))
  ));
  return preset?.id ?? "custom";
}

function permissionText(permissionOptions, copy, permissions = []) {
  if (!permissions.length) return copy.noAccess;
  const labels = new Map(permissionOptions.map((permission) => [permission.id, permission.label]));
  return permissions.map((permission) => labels.get(permission)).filter(Boolean).join(", ");
}

function zodErrors(error) {
  return Object.fromEntries(error.issues.map((issue) => [issue.path[0], issue.message]));
}

function formatRelativeLocal(iso, locale) {
  if (!iso) return "—";
  const date = new Date(iso);
  const seconds = Math.round((date.getTime() - Date.now()) / 1000);
  const abs = Math.abs(seconds);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  if (abs < 60) return rtf.format(Math.round(seconds), "second");
  if (abs < 3600) return rtf.format(Math.round(seconds / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(seconds / 3600), "hour");
  if (abs < 7 * 86400) return rtf.format(Math.round(seconds / 86400), "day");

  return date.toLocaleDateString(locale, { day: "2-digit", month: "short" });
}

function roleLabel(copy, role) {
  return role === "Manager" ? copy.roleManager : copy.roleStaff;
}

function statusLabel(copy, status) {
  const map = {
    Active: copy.statusActive,
    Invited: copy.statusInvited,
    Disabled: copy.statusDisabled,
  };
  return map[status] ?? copy.statusDisabled;
}

function isActiveMember(status) {
  return status === "Active";
}

function isInvitedMember(status) {
  return status === "Invited";
}

function isDisabledMember(status) {
  return status === "Disabled";
}

export default function Staff() {
  const { session } = useAuth();
  const { staff, branches, reload } = useWorkspace();
  const { language, locale, messages } = useLocalization();
  const billingCopy = billingAccessCopy[language] || billingAccessCopy.en;
  const copy = useMemo(() => ({ ...messages.staffPage, presetFullAccessDesc: billingCopy.fullAccess }), [messages.staffPage, billingCopy]);
  const settingsCopy = messages.settings;
  const allPermissionOptions = useMemo(() => getPermissionOptions(copy, settingsCopy, billingCopy), [copy, settingsCopy, billingCopy]);
  const permissionOptions = useMemo(
    () => allPermissionOptions.filter((permission) => hasPermission(session, permission.id)),
    [allPermissionOptions, session]
  );
  const presets = useMemo(
    () => getPresets(copy).map((preset) => ({
      ...preset,
      permissions: preset.permissions.filter((permission) => hasPermission(session, permission)),
    })).filter((preset) => preset.permissions.length > 0),
    [copy, session]
  );
  const inviteStaffSchema = useMemo(() => createInviteStaffSchema(copy), [copy]);
  const editStaffSchema = useMemo(() => createEditStaffSchema(copy), [copy]);
  const [members, setMembers] = useState(staff);
  const [query, setQuery] = useState("");
  const [activeView, setActiveView] = useState("team");
  const [editing, setEditing] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [inviteForm, setInviteForm] = useState(() => createInviteForm(presets, branches[0]?.id ?? ""));
  const [editForm, setEditForm] = useState(null);
  const [inviteErrors, setInviteErrors] = useState({});
  const [editErrors, setEditErrors] = useState({});

  useEffect(() => {
    setMembers(staff);
  }, [staff]);

  useEffect(() => {
    if (!inviteForm.branchId && branches[0]?.id) {
      setInviteForm((prev) => ({ ...prev, branchId: branches[0].id }));
    }
  }, [branches, inviteForm.branchId]);

  useEffect(() => {
    if (editing) {
      setEditForm({
        branchId: editing.branchId,
        name: editing.name,
        permissions: [...editing.permissions],
        presetId: findPresetId(presets, editing.permissions),
        role: editing.role,
      });
    } else {
      setEditForm(null);
    }
  }, [editing, presets]);

  useEffect(() => {
    setInviteForm((prev) => {
      const nextPresetId = presets.some((preset) => preset.id === prev.presetId) ? prev.presetId : presets[0].id;
      const nextPreset = presets.find((preset) => preset.id === nextPresetId) ?? presets[0];
      const nextPermissions = findPresetId(presets, prev.permissions) === "custom"
        ? prev.permissions
        : nextPreset.permissions;
      return {
        ...prev,
        permissions: nextPermissions,
        presetId: nextPresetId,
      };
    });
  }, [presets]);

  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return members;
    return members.filter((member) => (
      member.name.toLowerCase().includes(normalizedQuery) ||
      member.email.includes(normalizedQuery) ||
      member.loginEmail?.includes(normalizedQuery)
    ));
  }, [members, query]);

  const activeMembers = members.filter((member) => isActiveMember(member.status));
  const invitedMembers = members.filter((member) => isInvitedMember(member.status));
  const disabledMembers = members.filter((member) => isDisabledMember(member.status));
  const selected = filtered.find((member) => member.id === selectedId) ?? filtered[0] ?? null;

  async function sendInvite() {
    if (!session?.sessionId) return;
    const parsed = inviteStaffSchema.safeParse(inviteForm);
    if (!parsed.success) {
      setInviteErrors(zodErrors(parsed.error));
      return;
    }
    setInviteErrors({});

    try {
      const result = await inviteStaff(session.sessionId, {
        branchId: parsed.data.branchId,
        name: parsed.data.name,
        permissions: parsed.data.permissions,
        email: parsed.data.email,
        role: parsed.data.role,
      });
      await reload();
      toast.success(
        result?.staff?.loginEmail
          ? `${copy.inviteSent} Shop login: ${result.staff.loginEmail}`
          : copy.inviteSent,
      );
      setInviteForm(createInviteForm(presets, branches[0]?.id ?? ""));
      setInviteErrors({});
      setActiveView("team");
    } catch (error) {
      toast.error(error?.message || copy.couldNotInviteStaff);
    }
  }

  return (
    <PageContainer className="max-w-[1360px]">
      <div>
        <header className="flex flex-col gap-4 border-b border-border/70 pb-5 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/[0.08] text-primary ring-1 ring-primary/10">
              <UsersRound className="h-[18px] w-[18px]" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-semibold tracking-[-0.035em]">{copy.title}</h1>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  {activeMembers.length} {copy.active.toLowerCase()}
                </span>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{copy.subtitle}</p>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {members.length} {copy.members.toLowerCase()} · {invitedMembers.length} {copy.invited.toLowerCase()} · {disabledMembers.length} {copy.disabled.toLowerCase()}
              </p>
            </div>
          </div>
          <Button data-testid="staff-invite-open" className="h-10 rounded-xl px-4 shadow-sm" onClick={() => setActiveView("invite")}>
            <UserRoundPlus className="mr-1.5 h-4 w-4" /> {copy.inviteStaff}
          </Button>
        </header>

        <div className="mt-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <Tabs value={activeView} onValueChange={setActiveView} className="w-full sm:w-auto">
            <TabsList className="grid h-auto w-full grid-cols-2 rounded-2xl border border-border/70 bg-card/80 p-1.5 shadow-[0_10px_30px_-22px_rgba(15,23,42,0.45)] sm:w-[430px]">
              <StaffViewTab
                value="team"
                icon={UsersRound}
                label={copy.team}
                meta={`${activeMembers.length} ${copy.active.toLowerCase()}`}
                count={members.length}
                testId="staff-tab-team"
              />
              <StaffViewTab
                value="invite"
                icon={UserRoundPlus}
                label={copy.invite}
                meta={`${invitedMembers.length} ${copy.invited.toLowerCase()}`}
                count="+"
                testId="staff-tab-invite"
              />
            </TabsList>
          </Tabs>

          {activeView === "team" ? (
            <div className="relative w-full lg:max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="staff-search"
                placeholder={copy.searchPlaceholder}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className="h-10 rounded-xl bg-card pl-10 shadow-none focus-visible:ring-1 focus-visible:ring-primary/20 focus-visible:ring-offset-0"
              />
            </div>
          ) : null}
        </div>

        {activeView === "invite" ? (
          <section className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr)_300px]">
            <div className="rounded-2xl border bg-card p-5">
              <div className="mb-4 flex items-center gap-2">
                <UserRoundPlus className="h-4 w-4 text-primary" />
                <div className="text-sm font-semibold">{copy.prepareInvite}</div>
                <div className="h-px flex-1 bg-border/70" />
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <Field
                  label={copy.fullName}
                  placeholder={copy.fullNamePlaceholder}
                  testId="invite-name"
                  value={inviteForm.name}
                  error={inviteErrors.name}
                  onChange={(value) => {
                    setInviteErrors((prev) => ({ ...prev, name: undefined }));
                    setInviteForm((prev) => ({ ...prev, name: value }));
                  }}
                />
                <Field
                  label="Personal email · invitations & communication"
                  mono
                  placeholder="staff@example.com"
                  testId="invite-email"
                  value={inviteForm.email}
                  error={inviteErrors.email}
                  onChange={(value) => {
                    setInviteErrors((prev) => ({ ...prev, email: undefined }));
                    setInviteForm((prev) => ({ ...prev, email: value }));
                  }}
                />
                <SelectField
                  label={copy.branch}
                  testId="invite-branch"
                  value={inviteForm.branchId}
                  error={inviteErrors.branchId}
                  onChange={(value) => {
                    setInviteErrors((prev) => ({ ...prev, branchId: undefined }));
                    setInviteForm((prev) => ({ ...prev, branchId: value }));
                  }}
                  placeholder={copy.select}
                  options={branches.map((branch) => ({ label: branch.name, value: branch.id }))}
                />
                <RolePicker
                  copy={copy}
                  value={inviteForm.role}
                  error={inviteErrors.role}
                  onChange={(role) => {
                    setInviteErrors((prev) => ({ ...prev, role: undefined }));
                    setInviteForm((prev) => ({ ...prev, role }));
                  }}
                  testPrefix="invite"
                />
              </div>

              <div className="mt-4">
                <div className="mb-2 text-xs font-semibold text-muted-foreground">{copy.accessLevel}</div>
                <div className="divide-y divide-border/70 overflow-hidden rounded-xl border">
                  {presets.map((preset) => (
                    <PresetRow
                      key={preset.id}
                      active={inviteForm.presetId === preset.id}
                      preset={preset}
                      copy={copy}
                      onClick={() => {
                        setInviteErrors((prev) => ({ ...prev, permissions: undefined, presetId: undefined }));
                        setInviteForm((prev) => ({
                          ...prev,
                          permissions: preset.permissions,
                          presetId: preset.id,
                        }));
                      }}
                    />
                  ))}
                </div>
                {inviteErrors.permissions ? <p className="mt-2 text-xs font-medium text-destructive">{inviteErrors.permissions}</p> : null}
              </div>

              <div className="mt-4 flex justify-end">
                  <Button
                    data-testid="invite-send"
                    disabled={!inviteForm.branchId || !inviteForm.name.trim() || !inviteForm.email.trim() || !inviteForm.permissions.length}
                    className="h-10 rounded-xl px-4"
                    onClick={sendInvite}
                  >
                  <Mail className="mr-1.5 h-4 w-4" /> {copy.sendInvite}
                </Button>
              </div>
            </div>

            <InvitePreview inviteForm={inviteForm} branches={branches} permissionOptions={permissionOptions} copy={copy} />
          </section>
        ) : (
          <section className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className={filtered.length ? "overflow-hidden rounded-2xl border bg-card" : ""}>
              {filtered.map((member) => (
                <StaffCard
                  key={member.id}
                  branches={branches}
                  copy={copy}
                  member={member}
                  selected={selected?.id === member.id}
                  onEdit={member.permissions.every((permission) => hasPermission(session, permission)) ? () => setEditing(member) : null}
                  onSelect={() => setSelectedId(member.id)}
                />
              ))}

              {!filtered.length ? (
                <div className="rounded-2xl border bg-card px-5 py-14 text-center">
                  <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/[0.07] text-primary">
                    <UsersRound className="h-5 w-5" />
                  </div>
                  <div className="mt-3 text-sm font-semibold">{copy.noStaffFound}</div>
                  <p className="mt-1 text-xs text-muted-foreground">{copy.noStaffDescription}</p>
                  <Button className="mt-4 h-9 rounded-lg" onClick={() => setActiveView("invite")}>
                    {copy.inviteStaff}
                  </Button>
                </div>
              ) : null}
            </div>

            <StaffDetail
              selected={selected}
              branches={branches}
              permissionOptions={allPermissionOptions}
              locale={locale}
              copy={copy}
              onEdit={selected?.permissions.every((permission) => hasPermission(session, permission)) ? () => setEditing(selected) : null}
            />
          </section>
        )}
      </div>

      <Sheet open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <SheetContent className="w-full overflow-y-auto p-0 sm:max-w-md">
          {editing && (
            <>
              <SheetHeader className="border-b border-border p-6 pb-4">
                <SheetTitle>{editing.name}</SheetTitle>
                <SheetDescription className="font-mono text-xs">{editing.email} · {copy.lastSeen} {formatRelativeLocal(editing.lastSeen, locale)}</SheetDescription>
              </SheetHeader>
              <div className="space-y-5 p-6">
                <SelectField
                  label={copy.branch}
                  value={editForm?.branchId ?? ""}
                  error={editErrors.branchId}
                  onChange={(value) => {
                    setEditErrors((prev) => ({ ...prev, branchId: undefined }));
                    setEditForm((prev) => prev ? { ...prev, branchId: value } : prev);
                  }}
                  placeholder={copy.select}
                  options={branches.map((branch) => ({ label: branch.name, value: branch.id }))}
                />
                <RolePicker
                  copy={copy}
                  value={editForm?.role ?? roleOptions[0]}
                  error={editErrors.role}
                  onChange={(role) => {
                    setEditErrors((prev) => ({ ...prev, role: undefined }));
                    setEditForm((prev) => prev ? { ...prev, role } : prev);
                  }}
                  testPrefix="edit"
                />
                <div>
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{copy.accessLevel}</div>
                  <div className="space-y-2">
                    {presets.map((preset) => (
                      <PresetCard
                        key={preset.id}
                        active={editForm?.presetId === preset.id}
                        preset={preset}
                        copy={copy}
                        onClick={() => {
                          setEditErrors((prev) => ({ ...prev, permissions: undefined, presetId: undefined }));
                          setEditForm((prev) => prev ? {
                            ...prev,
                            permissions: preset.permissions,
                            presetId: preset.id,
                          } : prev);
                        }}
                      />
                    ))}
                  </div>
                  {editErrors.permissions ? <p className="mt-2 text-xs font-medium text-destructive">{editErrors.permissions}</p> : null}
                </div>
                <div>
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{copy.fineTunePermissions}</div>
                  <div className="space-y-2">
                    {permissionOptions.map((permission) => (
                      <label key={permission.id} className="flex cursor-pointer items-center gap-3 rounded-lg border border-border/80 bg-background px-3 py-2.5 text-sm hover:bg-muted/30">
                        <Checkbox
                          checked={editForm?.permissions?.includes(permission.id) ?? false}
                          onCheckedChange={(checked) => {
                            setEditForm((prev) => {
                              if (!prev) return prev;
                              const permissions = checked
                                ? Array.from(new Set([...prev.permissions, permission.id]))
                                : prev.permissions.filter((id) => id !== permission.id);
                              setEditErrors((current) => ({ ...current, permissions: undefined }));
                              return { ...prev, permissions, presetId: findPresetId(presets, permissions) };
                            });
                          }}
                        />
                        {permission.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <SheetFooter className="border-t border-border p-6">
                <Button
                  variant="ghost"
                  className="text-destructive"
                  onClick={async () => {
                    try {
                      await deleteStaff(session?.sessionId, editing.id);
                      await reload();
                      toast.success(copy.staffRemoved);
                      setEditing(null);
                    } catch (error) {
                      toast.error(error?.message || copy.couldNotRemoveStaff);
                    }
                  }}
                >
                  {copy.remove}
                </Button>
                <Button
                  onClick={async () => {
                    if (!editForm || !session?.sessionId) return;
                    const parsed = editStaffSchema.safeParse(editForm);
                    if (!parsed.success) {
                      setEditErrors(zodErrors(parsed.error));
                      return;
                    }
                    setEditErrors({});

                    try {
                      await updateStaff(session.sessionId, editing.id, {
                        branchId: parsed.data.branchId,
                        permissions: parsed.data.permissions,
                        role: parsed.data.role,
                      });
                      await reload();
                      toast.success(copy.saved);
                      setEditing(null);
                    } catch (error) {
                      toast.error(error?.message || copy.couldNotSaveStaff);
                    }
                  }}
                >
                  <ShieldCheck className="mr-1.5 h-4 w-4" /> {copy.saveChanges}
                </Button>
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>
    </PageContainer>
  );
}

function StaffViewTab({ value, icon: Icon, label, meta, count, testId }) {
  return (
    <TabsTrigger
      value={value}
      data-testid={testId}
      className="group h-[58px] justify-start gap-2.5 rounded-xl border border-transparent px-2.5 text-left shadow-none transition-all hover:bg-muted/50 focus-visible:!ring-0 focus-visible:!ring-offset-0 data-[state=active]:border-primary/80 data-[state=active]:!bg-primary data-[state=active]:!text-primary-foreground data-[state=active]:shadow-[0_12px_25px_-16px_hsl(var(--primary))] sm:px-3"
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-muted text-muted-foreground ring-1 ring-inset ring-border/70 transition-colors group-data-[state=active]:bg-white/[0.12] group-data-[state=active]:text-white group-data-[state=active]:ring-white/15">
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-xs font-semibold leading-4">{label}</span>
        <span className="block truncate text-[10px] font-normal leading-4 text-muted-foreground group-data-[state=active]:text-primary-foreground/70">{meta}</span>
      </span>
      <span className="grid h-6 min-w-6 shrink-0 place-items-center rounded-full bg-muted px-1.5 font-mono text-[10px] font-semibold text-muted-foreground transition-colors group-data-[state=active]:bg-white/15 group-data-[state=active]:text-white">
        {count}
      </span>
    </TabsTrigger>
  );
}

function Field({ label, placeholder, mono, testId, value, onChange, error }) {
  return (
    <div>
      <div className="mb-1.5 text-xs font-semibold text-muted-foreground">{label}</div>
      <Input
        data-testid={testId}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange?.(event.target.value)}
        className={`h-10 rounded-lg bg-muted/20 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 ${error ? "border-destructive" : ""} ${mono ? "font-mono tabular-nums" : ""}`}
      />
      {error ? <p className="mt-1.5 text-xs font-medium text-destructive">{error}</p> : null}
    </div>
  );
}

function SelectField({ label, testId, value, onChange, options, error, placeholder }) {
  return (
    <div>
      <div className="mb-1.5 text-xs font-semibold text-muted-foreground">{label}</div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          data-testid={testId}
          className={`h-10 rounded-lg bg-muted/20 shadow-none focus:ring-0 ${error ? "border-destructive" : ""}`}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error ? <p className="mt-1.5 text-xs font-medium text-destructive">{error}</p> : null}
    </div>
  );
}

function RolePicker({ copy, value, onChange, testPrefix, error }) {
  return (
    <div>
      <div className="mb-1.5 text-xs font-semibold text-muted-foreground">{copy.role}</div>
      <div className={`inline-flex w-full rounded-md border bg-muted/35 p-0.5 shadow-[0_10px_24px_rgba(15,23,42,0.04)] ${error ? "border-destructive" : "border-border/80"}`}>
        {roleOptions.map((role) => (
          <Button
            key={role}
            type="button"
            variant="ghost"
            data-testid={`${testPrefix}-role-${role.toLowerCase()}`}
            onClick={() => onChange(role)}
            className={`h-8 flex-1 gap-1.5 rounded-[5px] px-3 text-xs font-semibold hover:bg-background/70 ${
              value === role
                ? "border border-primary/25 bg-primary/10 text-primary hover:bg-primary/10 hover:text-primary"
                : "border border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {roleLabel(copy, role)}
          </Button>
        ))}
      </div>
      {error ? <p className="mt-1.5 text-xs font-medium text-destructive">{error}</p> : null}
    </div>
  );
}

function PresetCard({ active, preset, onClick, copy }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-xl border p-4 text-left transition-colors ${
        active
          ? "border-primary/30 bg-primary/[0.05]"
          : "border-border bg-card hover:bg-muted/30"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm font-semibold">{preset.label}</div>
        {active ? <CheckCircle2 className="h-4 w-4 text-primary" /> : null}
      </div>
      <p className="mt-1 text-xs leading-5 text-muted-foreground">{preset.description}</p>
      <div className="mt-3 text-[11px] font-semibold text-primary">{copy.permissionsCount(preset.permissions.length)}</div>
    </button>
  );
}

function PresetRow({ active, preset, onClick, copy }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center justify-between gap-3 px-3 py-3 text-left transition-colors ${
        active ? "bg-primary/[0.06]" : "bg-background hover:bg-muted/30"
      }`}
    >
      <div className="min-w-0">
        <div className={`text-sm font-semibold ${active ? "text-primary" : "text-foreground"}`}>{preset.label}</div>
        <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{preset.description}</div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span className="text-[11px] font-semibold text-muted-foreground">{preset.permissions.length}</span>
        {active ? <CheckCircle2 className="h-4 w-4 text-primary" /> : null}
      </div>
    </button>
  );
}

function InvitePreview({ inviteForm, branches, permissionOptions, copy }) {
  const branch = branches.find((item) => item.id === inviteForm.branchId);
  const preset = getPresets(copy).find((item) => item.id === inviteForm.presetId);
  return (
    <aside className="h-fit rounded-2xl border bg-card p-4 shadow-[0_10px_35px_-28px_rgba(15,70,68,0.45)]">
      <div className="flex items-center gap-3">
        <Avatar className="h-10 w-10">
          <AvatarFallback className="bg-primary/15 text-sm font-semibold text-primary">{getInitials(inviteForm.name, copy.roleStaff)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <div className="truncate text-base font-semibold">{inviteForm.name || copy.newTeammate}</div>
          <div className="truncate text-xs text-muted-foreground">{inviteForm.email || "Personal email"}</div>
        </div>
      </div>
      <div className="mt-4 space-y-2.5 text-sm">
        <PreviewRow icon={BriefcaseBusiness} label={copy.role} value={roleLabel(copy, inviteForm.role)} />
        <PreviewRow icon={MapPin} label={copy.branch} value={branch?.name ?? copy.selectBranch} />
        <PreviewRow icon={ShieldCheck} label={copy.access} value={preset?.label ?? copy.customAccess} />
      </div>
      <div className="mt-4 rounded-lg border border-border/80 bg-muted/20 p-3 text-xs leading-5 text-muted-foreground">
        {permissionText(permissionOptions, copy, inviteForm.permissions)}
      </div>
    </aside>
  );
}

function PreviewRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="h-4 w-4" />
        <span>{label}</span>
      </div>
      <span className="font-semibold">{value}</span>
    </div>
  );
}

function StaffCard({ branches, member, selected, onEdit, onSelect, copy }) {
  const branch = branches.find((item) => item.id === member.branchId);
  return (
    <div
      className={`relative overflow-hidden border-b last:border-b-0 transition-colors ${
        selected
          ? "bg-primary/[0.045]"
          : "bg-card hover:bg-muted/30"
      }`}
    >
      <button
        data-testid={`staff-row-${member.id}`}
        onClick={onSelect}
        className="flex w-full items-start gap-3 px-4 py-3.5 text-left"
      >
        <Avatar className="h-10 w-10">
          <AvatarFallback className="bg-primary/15 text-xs font-semibold text-primary">{getInitials(member.name)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold">{member.name}</div>
              <div className="mt-1 font-mono text-xs font-medium text-primary">{member.loginEmail}</div>
              <div className="mt-0.5 truncate text-[11px] text-muted-foreground">Contact · {member.email}</div>
            </div>
            <StatusBadge status={member.status} label={statusLabel(copy, member.status)} />
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
            <span>{roleLabel(copy, member.role)}</span>
            <span>·</span>
            <span>{branch?.name ?? copy.noBranch}</span>
            <span>·</span>
            <span>{copy.permissionsCount(member.permissions.length)}</span>
          </div>
        </div>
      </button>
      {onEdit ? <div className="flex justify-end px-4 pb-2.5">
        <button
          onClick={onEdit}
          className="inline-flex items-center gap-1.5 rounded-full bg-primary/[0.07] px-3 py-1.5 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/12"
        >
          {copy.editAccess}
          <MoreHorizontal className="h-3.5 w-3.5" />
        </button>
      </div> : null}
    </div>
  );
}

function StaffDetail({ selected, branches, permissionOptions, locale, copy, onEdit }) {
  if (!selected) {
    return (
      <aside className="hidden h-fit rounded-2xl border bg-card p-8 text-center text-muted-foreground lg:block">
        <UsersRound className="mx-auto h-8 w-8 opacity-50" />
        <div className="mt-3 text-sm">{copy.selectTeamMember}</div>
      </aside>
    );
  }

  const branch = branches.find((item) => item.id === selected.branchId);
  return (
    <aside className="h-fit rounded-2xl border bg-card p-5 shadow-[0_10px_35px_-28px_rgba(15,70,68,0.45)] lg:sticky lg:top-24">
      <div className="flex items-start justify-between gap-4">
        <Avatar className="h-11 w-11">
          <AvatarFallback className="bg-primary/15 text-sm font-semibold text-primary">{getInitials(selected.name)}</AvatarFallback>
        </Avatar>
        <StatusBadge status={selected.status} label={statusLabel(copy, selected.status)} />
      </div>
      <h2 className="mt-4 text-base font-semibold tracking-tight">{selected.name}</h2>
      <p className="mt-1 font-mono text-xs font-medium text-primary">{selected.loginEmail}</p>
      <p className="mt-1 truncate text-xs text-muted-foreground">Contact · {selected.email}</p>
      <div className="mt-5 space-y-3 text-sm">
        <PreviewRow icon={BriefcaseBusiness} label={copy.role} value={roleLabel(copy, selected.role)} />
        <PreviewRow icon={MapPin} label={copy.branch} value={branch?.name ?? copy.noBranch} />
        <PreviewRow icon={UserRound} label={copy.lastSeen} value={formatRelativeLocal(selected.lastSeen, locale)} />
      </div>
      <div className="mt-5 rounded-lg border border-border/80 bg-background/70 p-3 text-xs leading-5 text-muted-foreground">
        {permissionText(permissionOptions, copy, selected.permissions)}
      </div>
      {onEdit ? <Button className="mt-4 h-10 w-full rounded-xl" onClick={onEdit}>
        {copy.editAccess}
        <ArrowRight className="ml-1.5 h-4 w-4" />
      </Button> : null}
    </aside>
  );
}

function StatusBadge({ status, label = status }) {
  const map = {
    Active: "border-success/20 bg-success/15 text-success",
    Invited: "border-warning/20 bg-warning/15 text-warning",
    Disabled: "border-border bg-muted text-muted-foreground",
  };
  return <span className={`rounded-md border px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] ${map[status] ?? map.Disabled}`}>{label}</span>;
}
